import React from 'react';
import { Modal, Box, SpaceBetween, Button, Alert } from '@amzn/awsui-components-core';
import CopyToClipboard from '@amzn/awsui-components-core/copy-to-clipboard';
import LoadingBar from '@amzn/awsui-chat-components-core/loading-bar';
import ReactMarkdown from 'react-markdown';

interface TimeSlotSummaryModalProps {
  visible: boolean;
  onDismiss: () => void;
  isLoading: boolean;
  summaryText: string;
  error: string;
  onSendReminder?: () => void;
}

type ChildrenProp = { children: React.ReactNode };

const markdownComponents = {
  h1: ({ children }: ChildrenProp) => <h1 style={{ fontSize: '1.25em', fontWeight: 700, marginBottom: '0.5em' }}>{children}</h1>,
  h2: ({ children }: ChildrenProp) => <h2 style={{ fontSize: '1.1em', fontWeight: 600, marginBottom: '0.5em', marginTop: '0.75em' }}>{children}</h2>,
  h3: ({ children }: ChildrenProp) => <h3 style={{ fontSize: '1em', fontWeight: 600, marginBottom: '0.25em', marginTop: '0.5em' }}>{children}</h3>,
  p: ({ children }: ChildrenProp) => <p style={{ marginBottom: '0.75em' }}>{children}</p>,
  ul: ({ children }: ChildrenProp) => <ul style={{ paddingLeft: '1.5em', marginBottom: '0.75em', listStyleType: 'disc' }}>{children}</ul>,
  ol: ({ children }: ChildrenProp) => <ol style={{ paddingLeft: '1.5em', marginBottom: '0.75em', listStyleType: 'decimal' }}>{children}</ol>,
  li: ({ children }: ChildrenProp) => <li style={{ listStyleType: 'inherit', marginBottom: '0.25em' }}>{children}</li>,
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid #d1d5db', margin: '1em 0' }} />
};

export const TimeSlotSummaryModal: React.FC<TimeSlotSummaryModalProps> = ({ visible, onDismiss, isLoading, summaryText, error, onSendReminder }) => {
  if (!visible) return null;

  return (
    <Modal
      visible={true}
      onDismiss={onDismiss}
      header={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <span>✨ Scheduling Insights</span>
          {summaryText && !isLoading && !error && (
            <CopyToClipboard
              copyButtonAriaLabel="Copy summary"
              copyErrorText="Failed to copy"
              copySuccessText="Summary copied"
              textToCopy={summaryText}
              variant="icon"
            />
          )}
        </SpaceBetween>
      }
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {summaryText && !isLoading && !error && onSendReminder && (
              <Button variant="normal" iconName="envelope" onClick={onSendReminder}>
                Send Reminder
              </Button>
            )}
            <Button variant="link" onClick={onDismiss}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {isLoading ? (
        <SpaceBetween direction="vertical" size="m">
          <Box textAlign="center" color="text-body-secondary">
            Analyzing time slots and votes...
          </Box>
          <div className="ai-summary-loading-bar">
            <LoadingBar variant="gen-ai" />
          </div>
        </SpaceBetween>
      ) : error ? (
        <Alert type="error">{error}</Alert>
      ) : (
        <Box>
          <ReactMarkdown components={markdownComponents}>{summaryText}</ReactMarkdown>
        </Box>
      )}
    </Modal>
  );
};
